import { SITE_NAME, SITE_LOGO, buildCanonicalUrl, resolveImageUrl } from "../lib/seo";

const cleanText = (value = "") =>
  String(value)
    .replace(/\s+\|\s+PSP RCM.*$/, "")
    .trim();

export const normalizeEditorCodeHtml = (html = "") =>
  String(html || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ");

const blockTags = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "body",
  "div",
  "dl",
  "dt",
  "dd",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "li",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
]);

const voidTags = new Set(["br", "hr", "img", "input", "meta", "link", "source", "area", "base", "col", "embed", "param", "track", "wbr"]);

const indentLine = (level) => "  ".repeat(Math.max(0, level));

export const formatHtmlForCodeEditor = (html = "") => {
  const source = normalizeEditorCodeHtml(html).trim();
  if (!source) return "";

  if (typeof window === "undefined" || !window.DOMParser) {
    return source;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${source}</body>`, "text/html");
  const tokens = [];

  const walk = (node, depth) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent?.replace(/\s+/g, " ").trim();
        if (text) {
          tokens.push(`${indentLine(depth)}${text}`);
        }
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName.toLowerCase();
      const attrs = Array.from(child.attributes)
        .map((attr) => `${attr.name}="${attr.value.replace(/"/g, "&quot;")}"`)
        .join(" ");
      const open = attrs ? `<${tag} ${attrs}>` : `<${tag}>`;
      const close = `</${tag}>`;
      const isBlock = blockTags.has(tag);

      if (voidTags.has(tag)) {
        const voidTag = attrs ? `<${tag} ${attrs} />` : `<${tag} />`;
        tokens.push(`${indentLine(depth)}${voidTag}`);
        return;
      }

      if (isBlock) {
        tokens.push(`${indentLine(depth)}${open}`);
        walk(child, depth + 1);
        tokens.push(`${indentLine(depth)}${close}`);
        return;
      }

      const innerText = child.innerHTML.trim();
      if (!innerText) {
        tokens.push(`${indentLine(depth)}${open}${close}`);
        return;
      }

      const compact = child.outerHTML
        .replace(/>\s+</g, "><")
        .replace(/\s{2,}/g, " ")
        .trim();
      tokens.push(`${indentLine(depth)}${compact}`);
    });
  };

  walk(doc.body, 0);

  return tokens.join("\n");
};

export const createBlogAutoSchema = ({
  title = "",
  excerpt = "",
  metaDescription = "",
  image = "",
  publishedAt = "",
  slug = "",
  canonicalUrl = "",
}) => {
  const safeSlug = String(slug || "").replace(/^\/+|\/+$/g, "");
  const canonicalPath = canonicalUrl
    ? canonicalUrl
    : safeSlug
      ? `/blog/${safeSlug}`
      : "/blog";
  const resolvedCanonical = canonicalUrl || buildCanonicalUrl(canonicalPath);
  const resolvedTitle = cleanText(title);
  const resolvedDescription = cleanText(metaDescription || excerpt || title);
  const resolvedImage = image ? resolveImageUrl(image) : SITE_LOGO;

  return [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: resolvedTitle,
      description: resolvedDescription,
      image: resolvedImage,
      datePublished: publishedAt || undefined,
      dateModified: publishedAt || undefined,
      mainEntityOfPage: resolvedCanonical,
      author: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: SITE_LOGO,
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: buildCanonicalUrl("/"),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: buildCanonicalUrl("/blog"),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: resolvedTitle || "Blog Post",
          item: resolvedCanonical,
        },
      ],
    },
  ];
};

export const normalizeSchemaMarkup = (schemaMarkup = "") => {
  const value = String(schemaMarkup || "").trim();
  if (!value) return "";
  return value;
};
